import { useCallback, useEffect, useState } from "react";

const SESSION_MAX_AGE_MS = 12 * 3600 * 1000; // 12 hours default

export function useSession() {
  const [userData, setUserData] = useState(() => {
    try {
      // Prefer sessionStorage if valid
      const rawSession = sessionStorage.getItem("userData");
      if (rawSession) {
        const parsed = JSON.parse(rawSession);
        if (parsed.expiresAt && Date.now() <= parsed.expiresAt) return parsed;
        sessionStorage.removeItem("userData");
      }

      // Fallback to localStorage if valid
      const rawLocal = localStorage.getItem("userData");
      if (rawLocal) {
        const parsed = JSON.parse(rawLocal);
        if (parsed.expiresAt && Date.now() <= parsed.expiresAt) {
          sessionStorage.setItem("userData", JSON.stringify(parsed));
          return parsed;
        } else {
          localStorage.removeItem("userData");
        }
      }
      return null;
    } catch {
      sessionStorage.removeItem("userData");
      localStorage.removeItem("userData");
      return null;
    }
  });

  const saveUserData = useCallback((data, { preserveExpiry = false } = {}) => {
    if (preserveExpiry) {
      try {
        const existing = JSON.parse(localStorage.getItem("userData") || "null");
        if (existing?.expiresAt && existing.expiresAt > Date.now()) {
          sessionStorage.setItem("userData", JSON.stringify(existing));
          setUserData(existing);
          return;
        }
      } catch {
        console.warn("Failed to preserve expiry time from existing userData");
      }
    }
    const expiresAt = Date.now() + SESSION_MAX_AGE_MS;
    const withExpiry = { ...data, expiresAt };
    sessionStorage.setItem("userData", JSON.stringify(withExpiry));
    localStorage.setItem("userData", JSON.stringify(withExpiry));
    setUserData(withExpiry);
  }, []);

  const logout = useCallback((reason) => {
    sessionStorage.removeItem("userData");
    sessionStorage.removeItem("oauthState");
    sessionStorage.removeItem("originalPath");
    sessionStorage.removeItem("bypassmfacaptcha");
    sessionStorage.removeItem("lastSSORedirect");
    localStorage.removeItem("userData");
    setUserData(null);
    if (reason === "expired") {
      window.dispatchEvent(new CustomEvent("session-expired"));
    } else {
      // Logout manual
      window.dispatchEvent(new CustomEvent("session-logout"));
    }
  }, []);

  // Sync logout across tabs
  useEffect(() => {
    const onStorage = (e) => {
      if ((e.key === "userData" && !e.newValue) || e.key === null) {
        setUserData(null);
        window.dispatchEvent(new CustomEvent("session-logout"));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Auto-expire checker
  useEffect(() => {
    const check = () => {
      if (!userData) return;
      if (Date.now() > userData.expiresAt) {
        logout("expired");
      }
    };
    const interval = setInterval(check, 60 * 1000);
    window.addEventListener("focus", check);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", check);
    };
  }, [userData, logout]);

  return { userData, saveUserData, logout, SESSION_MAX_AGE_MS };
}
